/**
 * Lightweight canvas particle system for Survey Says — sparkle bursts on
 * answer reveals, confetti on game-over. Mirrors WhatIs ui/sparkles.ts so the
 * visual language matches across both games. Theme-driven palette via
 * `getThemeVar` (primary + accent + celebration).
 */

import { getThemeVar } from '../scene/lib/theme.js';

let sparkleColorsCache: readonly string[] | null = null;
let confettiColorsCache: readonly string[] | null = null;

function sparkleColors(): readonly string[] {
  if (sparkleColorsCache) return sparkleColorsCache;
  const primary = getThemeVar('primary', '#FFC43D');
  const accent = getThemeVar('accent', '#E91E63');
  sparkleColorsCache = [primary, '#FFE08A', accent, '#FFFFFF', '#FFB000'];
  return sparkleColorsCache;
}

function confettiColors(): readonly string[] {
  if (confettiColorsCache) return confettiColorsCache;
  const primary = getThemeVar('primary', '#FFC43D');
  const accent = getThemeVar('accent', '#E91E63');
  const celebration = getThemeVar('celebration', '#22C55E');
  confettiColorsCache = [primary, accent, celebration, '#FF3D8A', '#A86BFF', '#FFFFFF'];
  return confettiColorsCache;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  life: number;
  size: number;
  color: string;
  spin: number;
  twinkleOffset: number;
}

interface SparkleSeed {
  ox: number;
  oy: number;
  index: number;
  total: number;
  speed: number;
  palette: readonly string[];
}

export interface SparklesHandle {
  el: HTMLCanvasElement;
  burst: (opts: BurstOpts) => void;
  confetti: (opts?: ConfettiOpts) => void;
  dispose: () => void;
}

export interface BurstOpts {
  x: number;
  y: number;
  count?: number;
  speed?: number;
  colors?: string[];
}

export interface ConfettiOpts {
  x?: number;
  y?: number;
  count?: number;
  spread?: number;
}

const STAGE_W = 1920;
const STAGE_H = 1080;

function pickColor(palette: readonly string[]): string {
  const i = Math.floor(Math.random() * palette.length);
  return palette[i] ?? '#FFC43D';
}

function makeSparkleParticle(seed: SparkleSeed): Particle {
  const angle = (Math.PI * 2 * seed.index) / seed.total + Math.random() * 0.4;
  const v = (1.5 + Math.random() * 2.5) * seed.speed;
  return {
    x: seed.ox,
    y: seed.oy,
    vx: Math.cos(angle) * v * 80,
    vy: Math.sin(angle) * v * 80 - 60,
    age: 0,
    life: 0.9 + Math.random() * 0.7,
    size: 4 + Math.random() * 6,
    color: pickColor(seed.palette),
    spin: (Math.random() - 0.5) * 6,
    twinkleOffset: Math.random() * Math.PI * 2,
  };
}

function makeConfettiParticle(ox: number, oy: number, spread: number): Particle {
  const angle = -Math.PI / 2 + (Math.random() - 0.5) * spread * Math.PI;
  const v = 600 + Math.random() * 600;
  return {
    x: ox + (Math.random() - 0.5) * 200,
    y: oy,
    vx: Math.cos(angle) * v,
    vy: Math.sin(angle) * v,
    age: 0,
    life: 2.5 + Math.random() * 1.5,
    size: 8 + Math.random() * 8,
    color: pickColor(confettiColors()),
    spin: (Math.random() - 0.5) * 12,
    twinkleOffset: Math.random() * Math.PI * 2,
  };
}

function drawSparkle(ctx: CanvasRenderingContext2D, p: Particle, t: number): void {
  const alpha = (1 - t) * (0.7 + 0.3 * Math.sin(p.age * 12 + p.twinkleOffset));
  ctx.save();
  ctx.globalAlpha = Math.max(0, alpha);
  ctx.fillStyle = p.color;
  ctx.shadowColor = p.color;
  ctx.shadowBlur = 18;
  const r = p.size * (1 - t * 0.5);
  ctx.beginPath();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.age * p.spin);
  ctx.moveTo(-r, 0);
  ctx.lineTo(0, -r);
  ctx.lineTo(r, 0);
  ctx.lineTo(0, r);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawConfettiPiece(ctx: CanvasRenderingContext2D, p: Particle, t: number): void {
  const alpha = 1 - Math.pow(t, 3);
  ctx.save();
  ctx.globalAlpha = Math.max(0, alpha);
  ctx.fillStyle = p.color;
  ctx.translate(p.x, p.y);
  ctx.rotate(p.age * p.spin);
  ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
  ctx.restore();
}

export function createSparkles(mount: HTMLElement): SparklesHandle {
  const canvas = document.createElement('canvas');
  canvas.width = STAGE_W;
  canvas.height = STAGE_H;
  canvas.style.position = 'absolute';
  canvas.style.inset = '0';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.pointerEvents = 'none';
  canvas.style.zIndex = '50';
  canvas.setAttribute('data-overlay', 'sparkles');
  mount.appendChild(canvas);

  const ctx = canvas.getContext('2d')!;
  let particles: Particle[] = [];
  let confetti: Particle[] = [];
  let lastT = performance.now();
  let raf = 0;
  let running = true;

  function burst(opts: BurstOpts): void {
    const count = opts.count ?? 30;
    const speed = opts.speed ?? 1;
    const palette = opts.colors ?? sparkleColors();
    for (let i = 0; i < count; i++) {
      const seed: SparkleSeed = { ox: opts.x, oy: opts.y, index: i, total: count, speed, palette };
      particles.push(makeSparkleParticle(seed));
    }
  }

  function confettiBurst(opts: ConfettiOpts = {}): void {
    const ox = opts.x ?? STAGE_W / 2;
    const oy = opts.y ?? STAGE_H / 3;
    const count = opts.count ?? 120;
    const spread = opts.spread ?? 0.6;
    for (let i = 0; i < count; i++) {
      confetti.push(makeConfettiParticle(ox, oy, spread));
    }
  }

  function tickSparkles(dt: number): void {
    const alive: Particle[] = [];
    for (const p of particles) {
      p.age += dt;
      const t = p.age / p.life;
      if (t >= 1) continue;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 220 * dt;
      p.vx *= 0.97;
      drawSparkle(ctx, p, t);
      alive.push(p);
    }
    particles = alive;
  }

  function tickConfetti(dt: number): void {
    const alive: Particle[] = [];
    for (const p of confetti) {
      p.age += dt;
      const t = p.age / p.life;
      if (t >= 1) continue;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 900 * dt;
      p.vx *= 0.99;
      drawConfettiPiece(ctx, p, t);
      alive.push(p);
    }
    confetti = alive;
  }

  function step(now: number): void {
    if (!running) return;
    const dt = Math.min((now - lastT) / 1000, 0.06);
    lastT = now;
    ctx.clearRect(0, 0, STAGE_W, STAGE_H);
    tickSparkles(dt);
    tickConfetti(dt);
    raf = requestAnimationFrame(step);
  }
  raf = requestAnimationFrame(step);

  return {
    el: canvas,
    burst,
    confetti: confettiBurst,
    dispose() {
      running = false;
      cancelAnimationFrame(raf);
      canvas.remove();
    },
  };
}
